import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from '@mui/material';
import { useRouter } from 'next/router';
import { useKeycloak } from '@react-keycloak/ssr';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { getLastVisitedOrganization } from '@/session/lastVisitedOrganization';
import { runSessionLogout } from '@/session/logout';
import { getOrganizationList } from '@/session/organizationList';
import { isSessionTransition } from '@/session/operation';
import { FullPageError, FullPageStatus, SessionRecoveryFocusAnchor } from './FullPageSessionState';

export interface AuthGuardProps {
  children: React.ReactNode;
}

type HomeState = 'checking' | 'ready' | 'error';

export const AuthGuard: React.FC<AuthGuardProps> = ({ children }) => {
  const router = useRouter();
  const { keycloak, initialized } = useKeycloak();
  const { t } = useTranslation();
  const authenticated = keycloak?.authenticated ?? false;
  const [homeState, setHomeState] = useState<HomeState>('checking');
  const [retryNonce, setRetryNonce] = useState(0);
  const [retrying, setRetrying] = useState(false);
  // Counts failed attempts for the error Alert's re-announcement, and latches
  // whether the error surface was ever shown so recovery can move focus onto
  // the resolved content.
  const [failedAttempts, setFailedAttempts] = useState(0);
  const [hadError, setHadError] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const loginStartedRef = useRef(false);
  const routerRef = useRef(router);
  const keycloakRef = useRef(keycloak);

  useEffect(() => {
    routerRef.current = router;
    keycloakRef.current = keycloak;
  });

  // Adjust during render: the latch must be set in the same pass that renders
  // the error surface, and an effect calling setState directly is flagged by
  // the React Compiler lint.
  if (homeState === 'error' && !hadError) {
    setHadError(true);
  }

  const handleSignOut = useCallback(() => {
    const client = keycloakRef.current;
    if (signingOut || !initialized || !client || typeof (client as any).logout !== 'function') return;
    setSigningOut(true);
    void runSessionLogout(() => (client as any).logout()).catch(() => {
      setSigningOut(false);
      toast.error('Unable to sign out right now. Please try again.');
    });
  }, [initialized, signingOut]);

  useEffect(() => {
    if (!initialized || !router.isReady) return;
    if (!authenticated) {
      if (!loginStartedRef.current) {
        loginStartedRef.current = true;
        void (keycloakRef.current as any)?.login({ redirectUri: window.location.href });
      }
      return;
    }

    loginStartedRef.current = false;
    if (router.pathname !== '/') {
      return;
    }

    let mounted = true;
    void getOrganizationList({ force: retryNonce > 0 })
      .then((organizations) => {
        if (!mounted) return;
        setRetrying(false);
        const lastVisited = getLastVisitedOrganization();
        const destination = organizations.find((organization) => organization.name === lastVisited) ?? organizations[0];
        if (!destination) {
          setFailedAttempts(0);
          setHomeState('ready');
          return;
        }
        setHomeState('checking');
        // The redirect gets its own handler so a cancelled navigation is not
        // reported as an organization-list failure.
        void routerRef.current
          .replace(`/${destination.name}`)
          .then((navigated) => {
            // A blocked navigation resolves false without rejecting; leaving
            // homeState at 'checking' would strand "Redirecting…" forever.
            if (!navigated && mounted) setHomeState('error');
          })
          .catch((error: Error & { cancelled?: boolean }) => {
            if (mounted && !error?.cancelled) setHomeState('error');
          });
      })
      .catch((error: unknown) => {
        if (!mounted || isSessionTransition(error)) return;
        setRetrying(false);
        setFailedAttempts((value) => value + 1);
        setHomeState('error');
      });

    return () => {
      mounted = false;
    };
  }, [initialized, authenticated, retryNonce, router.isReady, router.pathname]);

  if (initialized && authenticated && router.isReady && router.pathname !== '/') {
    return <>{children}</>;
  }
  if (homeState === 'ready') {
    return (
      <>
        {hadError && <SessionRecoveryFocusAnchor label={t('Account access loaded')} />}
        {children}
      </>
    );
  }

  if (homeState === 'error' && initialized && authenticated) {
    return (
      <FullPageError
        message={t('We could not load your organizations. Your account access has not been determined.')}
        retrying={retrying}
        attempt={failedAttempts}
        onRetry={() => {
          setRetrying(true);
          setRetryNonce((value) => value + 1);
        }}
        secondaryAction={
          <Button aria-disabled={signingOut} color='inherit' onClick={handleSignOut}>
            {signingOut ? t('Signing out…') : t('Sign out')}
          </Button>
        }
      />
    );
  }

  return <FullPageStatus message={t(initialized ? 'Redirecting…' : 'Checking your session…')} />;
};
